import { ApiError, type components } from "@sos/api-client";
import { queryOptions } from "@tanstack/react-query";

import { api } from "../api/client";
import { setBusinessToday } from "../lib/format";

export type Me = components["schemas"]["MeOut"];
export type Area = "seller" | "owner";

/**
 * Current session (`GET /v1/auth/me`, BE-05). `null` means no session (401). The only source of
 * role and locale (FE-01 §6.2); fetched once per start, stale after 5 min.
 */
export const meQuery = queryOptions({
  queryKey: ["me"] as const,
  queryFn: async (): Promise<Me | null> => {
    try {
      const { data } = await api.GET("/v1/auth/me");
      // FE-03 FR-FE03-23: "today" for relative dates is the API's business date.
      setBusinessToday(data?.today);
      return data ?? null;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  },
  staleTime: 5 * 60_000,
  meta: { persist: true },
});

/** The area a session belongs to: owners use the portal, sellers and admins the seller app. */
export function areaOf(me: Me): Area {
  return me.user.role === "owner" ? "owner" : "seller";
}

export function isAdmin(me: Me | null | undefined): boolean {
  return Boolean(me && (me.user.is_admin || me.user.role === "admin"));
}

/**
 * "Prospectar una zona" (FE-06 FR-FE06-30): the backend decides per role (`permissions.prospect_zone`)
 * so the operator can restrict Places spend. A session cached before the field existed falls back to
 * admins only.
 */
export function canProspectZone(me: Me | null | undefined): boolean {
  if (!me) return false;
  const permissions = me.permissions as Me["permissions"] | undefined;
  return permissions ? permissions.prospect_zone : isAdmin(me);
}
